import { GlobalStore } from "@roc/feature-app-core";
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { InspectionPhotosService } from "../services/inspectionPhotosService";
import { InspectionDetails, InspectionPhoto } from "../types/types";
import PlaceHolderImage from "../assets/Placeholder.svg";

import { flatten, GENERIC_ERROR_MESSAGE } from "@roc/feature-utils";

export class InspectionPhotosStore {
  private globalStore: GlobalStore;
  inspectionPhotosService: InspectionPhotosService;
  inspectionId: number;
  inspectionDetails: InspectionDetails;
  inspectionTaskPhotos: Record<string, InspectionPhoto[]>;
  cachedImages: Record<string, string>;
  properties: any[];
  draws: any[];
  isLoadingMoveData: boolean;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.inspectionPhotosService = new InspectionPhotosService();

    makeObservable(this, {
      reset: action,
      loadInspectionPhotos: flow,
      fetchPhoto: flow,
      inspectionTaskPhotos: observable,
      inspectionPhotos: computed,
      inspectionDetails: observable,
      photoHasManualValidation: action,
      totalPhotos: computed,
      photosOk: computed,
      photosWithIssues: computed,
      automaticallyValidatedPhotos: computed,
      manuallyApprovedPhotos: computed,
      setPhotoPreferredRotation: action,
      overrideLocationValidation: flow,
      overrideLocationValidationForAllPhotos: flow,
      updateInspectionGeolocation: flow,
      properties: observable,
      draws: observable,
      isLoadingMoveData: observable,
      fetchPropertiesForMove: flow,
      fetchDrawsForMove: flow,
      resetMoveData: action,
      clearDraws: action,
      movePhotos: flow,
      copyPhotos: flow,
      setPhotoReported: flow,
      canReportPhotos: computed,
    });
    this.reset();
  }


  *loadInspectionPhotos(inspectionDetails: InspectionDetails) {
    this.inspectionId = inspectionDetails.inspectionId;
    this.inspectionDetails = inspectionDetails;
    try {
      const response: any = yield this.inspectionPhotosService.getInspectionPhotosByInspectionId(this.inspectionId);

      if (response?.data?.status === "OK") {
        const data = response?.data?.data || {};

        yield this.fetchThumbnailsBulk(Object.values(data));

        this.inspectionTaskPhotos = data;
      } else {
        throw new Error("There was an error while retrieving the inspection photos.");
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving the inspection photos.',
      });
    }
  }

  *updateInspectionGeolocation(latitude, longitude) {
    try {
      this.inspectionDetails.latitude = latitude;
      this.inspectionDetails.longitude = longitude;
      yield this.inspectionPhotosService.updateInspectionGeoLocationData({
        inspectionId: this.inspectionDetails.inspectionId,
        latitude,
        longitude,
      });

      const inspectionPhotosResponse: any = yield this.inspectionPhotosService.getInspectionPhotosByInspectionId(
        this.inspectionId
      );
      const data = inspectionPhotosResponse?.data?.data || {};
      const photos = flatten(Object.values(data));
      photos.forEach(photo => {
        const current = this.inspectionPhotos.find(x => x.inspectionPhotoId === photo.inspectionPhotoId);
        photo.image = current?.image;
        photo.thumbnailImage = current?.thumbnailImage;
      });
      this.inspectionTaskPhotos = data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  async fetchThumbnails(data: any) {
    await Promise.all(
      Object.values(data).map(async (photos: any[]) => {
        await Promise.all(
          photos.map(async (photo: any) => {
            try {
              const thumbnailBlob = await this.inspectionPhotosService.getInspectionPhotoThumbnail(photo.inspectionPhotoUuid)

              photo.image = URL.createObjectURL(thumbnailBlob.data as any);
              photo.thumbnailImage = URL.createObjectURL(thumbnailBlob.data as any);
            } catch (err) {
              console.error(`Failed to load photo ${photo.inspectionPhotoUuid}`, err);
              photo.image = PlaceHolderImage;
              photo.thumbnailImage = PlaceHolderImage;
            }
          })
        );
      })
    );
  }

  async fetchThumbnailsBulk(data: any) {
    const photos = flatten(Object.values(data)) as InspectionPhoto[];
    if (!photos?.length) return;
    const thumbnailsResponse = await this.inspectionPhotosService.getInspectionThumbnails(this.inspectionId);
    photos.forEach(photo => {
      const thumbnailBlob = thumbnailsResponse.get(photo.inspectionPhotoUuid) as File;
      const url = thumbnailBlob ? URL.createObjectURL(thumbnailBlob) : PlaceHolderImage;
      photo.image = url;
      photo.thumbnailImage = url;
    })
  }

  *fetchPhoto(inspectionPhotoId: number) {
    try {
      const photo = this.inspectionPhotos.find(p => p.inspectionPhotoId === inspectionPhotoId);
      const photoUuid = photo.inspectionPhotoUuid;
      if (!this.cachedImages[photoUuid]) {
        const photoBlob = yield this.inspectionPhotosService.getInspectionPhoto(photoUuid);
        const blobURL = URL.createObjectURL(photoBlob.data as any);
        photo.image = blobURL;
        this.cachedImages[photoUuid] = blobURL;
        this.inspectionTaskPhotos = { ...this.inspectionTaskPhotos };
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  reset() {
    this.inspectionId = null;
    this.inspectionDetails = null;
    this.cachedImages = {};
    this.inspectionTaskPhotos = {};
    this.properties = [];
    this.draws = [];
    this.isLoadingMoveData = false;
  }

  get totalPhotos() {
    return this.inspectionPhotos?.length || 0;
  }

  get photosOk() {
    if (!this.inspectionPhotos) {
      return 0;
    }
    return this.inspectionPhotos.filter(photo => !this.photoHasIssues(photo)).length;
  }

  get photosWithIssues() {
    if (!this.inspectionPhotos) {
      return 0;
    }
    return this.inspectionPhotos.filter(photo => this.photoHasIssues(photo)).length;
  }

  get automaticallyValidatedPhotos() {
    if (!this.inspectionPhotos) {
      return 0;
    }
    return this.inspectionPhotos.filter(photo => !this.photoHasIssues(photo) && !this.photoHasManualValidation(photo)).length;
  }

  get manuallyApprovedPhotos() {
    if (!this.inspectionPhotos) {
      return 0;
    }
    return this.inspectionPhotos.filter(photo => !this.photoHasIssues(photo) && this.photoHasManualValidation(photo)).length;
  }

  get inspectionPhotos(): InspectionPhoto[] {
    return flatten(Object.values(this.inspectionTaskPhotos ?? {}));
  }

  photoHasIssues(inspectionPhoto: InspectionPhoto) {
    if (!inspectionPhoto || !this.inspectionDetails) {
      return false;
    }
    const isLocationValid = inspectionPhoto.isLocationValid || inspectionPhoto.isLocationValidManualOverride;
    return !inspectionPhoto.isHashValid || !isLocationValid || !inspectionPhoto.isSignatureValid;
  }

  photoHasManualValidation(inspectionPhoto: InspectionPhoto) {
    return !inspectionPhoto.isLocationValid && inspectionPhoto.isLocationValidManualOverride;
  }

  setPhotoPreferredRotation(inspectionPhotoId: string, rotationDegrees: number) {
    try {
      const inspectionPhoto = this.inspectionPhotos.find(photo => photo.inspectionPhotoId === +inspectionPhotoId);
      if (inspectionPhoto) {
        inspectionPhoto.preferredRotation = rotationDegrees;

        for (const photos of Object.values(this.inspectionTaskPhotos || {})) {
          photos.forEach(photo => {
            if (photo.inspectionPhotoId === +inspectionPhotoId) {
              photo.preferredRotation = rotationDegrees;
              this.inspectionPhotosService.patchInspectionPhotoRotation(+inspectionPhotoId, rotationDegrees);
              return;
            }
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
  }

  *overrideLocationValidation(_inspectionPhotoIds, isValid) {
    try {
      const inspectionPhotoIds = _inspectionPhotoIds.map(id => parseInt(id));

      const data = inspectionPhotoIds.map(inspectionPhotoId => ({
        inspectionPhotoId,
        isLocationValidManualOverride: isValid,
      }));
      yield this.inspectionPhotosService.overrideInspectionPhotoValidation(data);

      this.inspectionPhotos
        .filter(photo => inspectionPhotoIds.includes(photo.inspectionPhotoId))
        .forEach(photo => (photo.isLocationValidManualOverride = isValid));

      flatten(Object.values(this.inspectionTaskPhotos || {}))
        .filter(photo => inspectionPhotoIds.includes(photo.inspectionPhotoId))
        .forEach(photo => (photo.isLocationValidManualOverride = isValid));

    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *overrideLocationValidationForAllPhotos() {
    const inspectionPhotoIds = this.inspectionPhotos.map(
      x => x.inspectionPhotoId
    );
    this.overrideLocationValidation(inspectionPhotoIds, true);
  }

  resetMoveData() {
    this.properties = [];
    this.draws = [];
    this.isLoadingMoveData = false;
  }

  clearDraws() {
    this.draws = [];
  }

  *fetchPropertiesForMove(loanId: string) {
    if (!loanId) return;

    try {
      this.isLoadingMoveData = true;
      const response = yield this.inspectionPhotosService.getPropertiesByLoanId(
        loanId
      );
      if (response?.data?.data) {
        this.properties = response.data.data;
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load properties',
      });
    } finally {
      this.isLoadingMoveData = false;
    }
  }

  *fetchDrawsForMove(loanId: string, propertyId: string) {
    if (!loanId || !propertyId) return;

    try {
      this.isLoadingMoveData = true;
      const response = yield this.inspectionPhotosService.getDrawsByPropertyId(
        loanId,
        propertyId
      );
      if (response?.data?.data) {
        this.draws = response.data.data;
      }
    } catch (error) {
      console.error('Error fetching draws:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load draws',
      });
    } finally {
      this.isLoadingMoveData = false;
    }
  }

  *movePhotos(
    selectedPhotos: number[],
    sourceInspectionId: number,
    destinationInspectionId: number
  ) {
    try {
      this.isLoadingMoveData = true;
      const payload = {
        selectedPhotos,
        sourceInspectionId,
        destinationInspectionId,
      };
      yield this.inspectionPhotosService.movePhotos(payload);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Photos moved successfully',
      });
      yield this.loadInspectionPhotos(this.inspectionDetails);
    } catch (error) {
      console.error('Error moving photos:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message:
          error?.response?.data?.error?.message || 'Failed to move photos',
      });
    } finally {
      this.isLoadingMoveData = false;
    }
  }

  *copyPhotos(
    selectedPhotos: number[],
    sourceInspectionId: number,
    destinationInspectionId: number
  ) {
    try {
      this.isLoadingMoveData = true;
      const payload = {
        selectedPhotos,
        sourceInspectionId,
        destinationInspectionId,
      };
      yield this.inspectionPhotosService.copyPhotos(payload);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Photos copied successfully',
      });
    } catch (error) {
      console.error('Error copying photos:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message:
          error?.error?.response?.data?.error?.message || 'Failed to copy photos',
      });
    } finally {
      this.isLoadingMoveData = false;
    }
  }

  *setPhotoReported(inspectionPhotoId: number, reported: boolean) {
    try {
      const inspectionPhoto = this.inspectionPhotos.find(photo => photo.inspectionPhotoId === inspectionPhotoId);
      if (inspectionPhoto) {
        yield this.inspectionPhotosService.patchInspectionPhotoReported(inspectionPhotoId, reported);
        this.inspectionTaskPhotos = { ...this.inspectionTaskPhotos };
        inspectionPhoto.reported = reported;
      }
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  get canReportPhotos() {
    return this.globalStore.isInternalPortal;
  }

}
